import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  classEnrollmentFieldIds,
  type ClassEnrollmentStatus,
} from "@/server/platform/db/schema/class-enrollments";
import { formatDateTime } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  Textarea,
  confirm,
  toast,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, SparklesIcon, UserIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type { ClassEnrollmentSimilarStudentDTO } from "../api/client";
import {
  useClassEnrollmentQuery,
  useClassEnrollmentSimilarStudentsQuery,
  useConvertClassEnrollmentMutation,
  useUpdateClassEnrollmentStatusMutation,
} from "../api/queries";
import { canTransitionClassEnrollmentStatus } from "../../domain/lib/enrollment-rules";
import {
  CLASS_ENROLLMENT_GENDER_LABELS,
  CLASS_ENROLLMENT_STATUS_LABELS,
  CLASS_ENROLLMENT_STATUS_VARIANTS,
} from "../lib/status";

const ACTION_STATUSES = ["contacted", "confirmed", "rejected", "duplicate"] as const;

export function ClassEnrollmentDetailPage() {
  const { id } = useParams({ from: "/app/class-enrollments/$id" });
  const nav = useNavigate({ from: "/app/class-enrollments/$id" });
  const { data: detail, isLoading } = useClassEnrollmentQuery(id);
  const updateMutation = useUpdateClassEnrollmentStatusMutation();
  const convertMutation = useConvertClassEnrollmentMutation();
  const canUpdate = useActionPermission(["class-enrollments:update"]);
  const canConvert = useActionPermission(["class-enrollments:convert"]);
  const canSell = useActionPermission(["packages:sell"]);
  const [adminNotes, setAdminNotes] = useState("");
  const [linkStudentId, setLinkStudentId] = useState<string | null>(null);

  const canRunConvert =
    !!detail &&
    (detail.status === "confirmed" || detail.status === "contacted") &&
    !detail.studentId;
  const { data: similarStudentsData, isLoading: similarStudentsLoading } =
    useClassEnrollmentSimilarStudentsQuery(id, canRunConvert && canConvert);
  const similarMatches = similarStudentsData?.matches ?? [];

  useEffect(() => {
    setAdminNotes(detail?.adminNotes ?? "");
  }, [detail?.adminNotes, detail?.id]);

  const breadcrumb = (
    <Breadcrumb
      items={[
        { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
        { label: "ໃບສະໝັກຮຽນ", to: "/app/class-enrollments" },
        { label: "ລາຍລະອຽດ" },
      ]}
    />
  );

  if (isLoading && !detail) {
    return (
      <>
        <Header>{breadcrumb}</Header>
        <Main>
          <p className="py-24 text-center text-muted-foreground">ກຳລັງໂຫຼດ...</p>
        </Main>
      </>
    );
  }

  if (!detail) {
    return (
      <>
        <Header>{breadcrumb}</Header>
        <Main>
          <p className="py-12 text-center text-muted-foreground">ບໍ່ພົບໃບສະໝັກ</p>
        </Main>
      </>
    );
  }

  const currentStatus = detail.status as ClassEnrollmentStatus;
  const availableStatusActions = ACTION_STATUSES.filter(
    (status) =>
      canTransitionClassEnrollmentStatus(currentStatus, status) && status !== currentStatus,
  );

  const handleStatusChange = async (status: (typeof ACTION_STATUSES)[number]) => {
    const ok = await confirm({
      title: CLASS_ENROLLMENT_STATUS_LABELS[status] ?? status,
      description:
        status === "confirmed"
          ? `ຢືນຢັນວ່າ ${detail.fullName} ມາຢືນຢັນທີ່ເຄົາເຕີແລ້ວ?`
          : `ປ່ຽນສະຖານະຂອງ ${detail.fullName}?`,
      actionText: CLASS_ENROLLMENT_STATUS_LABELS[status] ?? status,
      ActionProps:
        status === "rejected" || status === "duplicate"
          ? { variant: "destructive" }
          : undefined,
    });
    if (!ok) return;
    try {
      await updateMutation.mutateAsync({
        id,
        body: { status, adminNotes: adminNotes || null },
      });
      toast.success("ອັບເດດສະຖານະສຳເລັດ");
    } catch {
      toast.error("ອັບເດດສະຖານະລົ້ມເຫຼວ");
    }
  };

  const runConvert = async (body: {
    linkExistingStudentId?: string;
    forceCreateNew?: boolean;
  }) => {
    await convertMutation.mutateAsync({ id, body });
    toast.success("ປ່ຽນເປັນນັກຮຽນສຳເລັດ — ຂາຍແພັກເກດຕໍ່ໄດ້ຈາກໜ້ານັກຮຽນ");
  };

  const handleConvert = async () => {
    try {
      if (similarMatches.length > 0 && !linkStudentId) {
        const ok = await confirm({
          title: "ສ້າງນັກຮຽນໃໝ່?",
          description: `ພົບນັກຮຽນທີ່ຄ້າຍກັນ ${similarMatches.length} ຄົນ ຢືນຢັນສ້າງໃໝ່ແທນການຜູກ?`,
          actionText: "ສ້າງໃໝ່",
          ActionProps: { variant: "destructive" },
        });
        if (!ok) return;
        await runConvert({ forceCreateNew: true });
        return;
      }
      await runConvert({ linkExistingStudentId: linkStudentId ?? undefined });
    } catch (error: unknown) {
      const err = error as Error & { matches?: ClassEnrollmentSimilarStudentDTO[] };
      if (err.matches?.length) {
        setLinkStudentId(err.matches[0]?.id ?? null);
        toast.error("ພົບນັກຮຽນຄ້າຍກັນ — ເລືອກຜູກ ຫຼື ຢືນຢັນສ້າງໃໝ່");
        return;
      }
      toast.error("ປ່ຽນເປັນນັກຮຽນລົ້ມເຫຼວ");
    }
  };

  const answerText = (fieldId: string, value: string | null) => {
    if (fieldId === classEnrollmentFieldIds.gender && value) {
      return CLASS_ENROLLMENT_GENDER_LABELS[value] ?? value;
    }
    return value?.trim() || "—";
  };

  return (
    <>
      <Header>{breadcrumb}</Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-bold text-2xl">ໃບສະໝັກຮຽນ</h1>
              <Badge variant={CLASS_ENROLLMENT_STATUS_VARIANTS[detail.status] ?? "outline"}>
                {CLASS_ENROLLMENT_STATUS_LABELS[detail.status] ?? detail.status}
              </Badge>
            </div>
            <p className="mt-1 font-mono text-sm">ເລກອ້າງອີງ {detail.id}</p>
          </div>
          <Button variant="outline" onClick={() => nav({ to: "/app/class-enrollments" })}>
            <ArrowLeftIcon className="size-4" />
            ກັບໄປລາຍການ
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 font-normal text-muted-foreground text-sm">
                  <UserIcon className="size-4" />
                  ຄຳຕອບ
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {detail.answers.map((answer) => (
                  <div key={answer.fieldId}>
                    <p className="font-medium text-muted-foreground text-xs uppercase">
                      {answer.label}
                    </p>
                    <p className="font-semibold">{answerText(answer.fieldId, answer.value)}</p>
                  </div>
                ))}
                <div>
                  <p className="font-medium text-muted-foreground text-xs uppercase">ວັນທີສະໝັກ</p>
                  <p className="font-semibold">{formatDateTime(detail.submittedAt)}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 font-normal text-muted-foreground text-sm">
                  <SparklesIcon className="size-4" />
                  ຄລາດສ໌ທີ່ເລືອກ
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {detail.classes.map((item) => (
                  <Badge key={item.templateId} variant="secondary">
                    {item.classTypeName} {item.levelName}
                  </Badge>
                ))}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="font-normal text-muted-foreground text-sm">
                  ຈັດການໃບສະໝັກ
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="adminNotes">ໝາຍເຫດທີມງານ</Label>
                  <Textarea
                    id="adminNotes"
                    value={adminNotes}
                    onChange={(event) => setAdminNotes(event.target.value)}
                    disabled={!canUpdate}
                  />
                </div>

                {canUpdate && availableStatusActions.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {availableStatusActions.map((status) => (
                      <Button
                        key={status}
                        size="sm"
                        variant={status === "confirmed" ? "default" : "outline"}
                        disabled={updateMutation.isPending}
                        onClick={() => handleStatusChange(status)}
                      >
                        {status === "confirmed"
                          ? "ຢືນຢັນທີ່ເຄົາເຕີ"
                          : CLASS_ENROLLMENT_STATUS_LABELS[status]}
                      </Button>
                    ))}
                  </div>
                ) : null}

                {canRunConvert && canConvert ? (
                  <div className="space-y-3 border-t pt-4">
                    <p className="font-medium text-sm">ປ່ຽນເປັນນັກຮຽນ</p>
                    {similarStudentsLoading ? (
                      <p className="text-muted-foreground text-xs">ກຳລັງກວດຫານັກຮຽນຄ້າຍກັນ...</p>
                    ) : similarMatches.length > 0 ? (
                      <div className="space-y-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
                        <p className="text-amber-700 text-xs dark:text-amber-400">
                          ພົບນັກຮຽນຄ້າຍກັນ — ເລືອກຜູກກ່ອນ ຫຼື ສ້າງໃໝ່
                        </p>
                        {similarMatches.map((match) => (
                          <Button
                            key={match.id}
                            type="button"
                            size="sm"
                            variant={linkStudentId === match.id ? "default" : "outline"}
                            className="h-auto w-full justify-start py-2"
                            onClick={() =>
                              setLinkStudentId(linkStudentId === match.id ? null : match.id)
                            }
                          >
                            <span className="flex w-full flex-col items-start text-left">
                              <span>{match.name}</span>
                              <span className="font-normal text-muted-foreground text-xs">
                                {match.memberId}
                                {match.phoneNumber ? ` · ${match.phoneNumber}` : ""}
                                {` · ຄ້າຍ ${match.similarityPercent}%`}
                              </span>
                            </span>
                          </Button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-xs">ບໍ່ພົບນັກຮຽນຄ້າຍກັນ</p>
                    )}
                    <Button
                      className="w-full"
                      disabled={convertMutation.isPending || similarStudentsLoading}
                      onClick={handleConvert}
                    >
                      {linkStudentId ? "ຜູກກັບນັກຮຽນທີ່ມີຢູ່" : "ສ້າງນັກຮຽນໃໝ່"}
                    </Button>
                  </div>
                ) : null}

                {currentStatus === "pending" && canConvert ? (
                  <p className="text-muted-foreground text-xs">
                    ຢືນຢັນທີ່ເຄົາເຕີກ່ອນ ຈຶ່ງປ່ຽນເປັນນັກຮຽນໄດ້
                  </p>
                ) : null}

                {detail.student ? (
                  <div className="space-y-2 border-t pt-4">
                    <p className="text-sm">
                      ນັກຮຽນ {detail.student.memberId} · {detail.student.name}
                    </p>
                    <Button
                      className="w-full"
                      onClick={() =>
                        nav({ to: "/app/students/$id", params: { id: detail.student!.id } })
                      }
                    >
                      ເປີດໜ້ານັກຮຽນ
                    </Button>
                    {canSell ? (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() =>
                          nav({
                            to: "/app/sell",
                            search: { type: "package", studentId: detail.student!.id },
                          })
                        }
                      >
                        ຂາຍແພັກເກດຕໍ່
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </div>
        </div>
      </Main>
    </>
  );
}
